import {
  BRAND_LIST,
  COUNTRY,
  GABrandList,
  GAFilterType,
  GAGlobalOptionType,
  GAStylingType,
  GATrackEventType,
  LoginStateType,
  PageViewBaseOption,
  ProductGAInfoType
} from "@type/index";
import { getIsECGuestLoggedIn, getIsLoggedIn } from "@util/helpers";
import {
  ADD_TO_STYLING_WISHLIST,
  ADD_TO_WISHLIST,
  APPLY_FILTER,
  CLICK_RECOMMENDED_USER,
  getBaseGAItemProps,
  getGAPropsForAddWishlist,
  getGAPropsForRemoveWishlist,
  REMOVE_FROM_STYLING_WISHLIST,
  REMOVE_FROM_WISHLIST,
  SELECT_ITEM,
  SELECT_STYLING,
  splitTagList,
  VIEW_ITEM_LIST,
  VIEW_STYLING
} from "./index";

export const isClient = typeof window !== "undefined";
// This is used in GA event when sortType, or other parameters are not present, to avoid sending null or empty values
export const NONE = "(none)";

let firstPageView = true;
export function checkPreviousPageExists() {
  if (!isClient) {
    return false;
  }

  if (!firstPageView) {
    return true;
  }
  firstPageView = false;

  if (
    window.performance
      .getEntriesByType("navigation")
      .map((nav: any) => nav.type)
      .includes("reload")
  ) {
    return true;
  }

  return !!(
    document.referrer &&
    document.referrer.includes(new URL(window.location.href).hostname)
  );
}

/*
  GU brand must be capitalized for Google Analytics,
  while the internal BRAND_LIST is lowercase "gu"
*/
export const transformBrandForGoogleAnalytics = (
  brand: string
): GABrandList => {
  switch (brand) {
    case BRAND_LIST.UQ:
      return GABrandList.UQ;
    case BRAND_LIST.GU:
      return GABrandList.GU;
    case BRAND_LIST.PL:
      return GABrandList.PL;
    default:
      return GABrandList.UQ;
  }
};

/**
 * sendPageViewForWave2 passes data into window.dataLayer and run callback functions which are injected by native apps if needed.
 * It is called for tracking page view events and virtual page view events.
 *
 * @param data
 */
export const sendPageViewForWave2 = (data: any) => {
  if (!isClient) {
    return;
  }
  if (!window.dataLayer) {
    return;
  }

  if (
    window.UQCustomerAppAndroid &&
    window.UQCustomerAppAndroid.receiveAnalyticsData
  ) {
    // Android App
    window.UQCustomerAppAndroid.receiveAnalyticsData(JSON.stringify(data));
  } else if (
    window.webkit &&
    window.webkit.messageHandlers.receiveAnalyticsData &&
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage
  ) {
    // iOS App
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage(data);
  }
  // Web Browser and Native App
  window.dataLayer.push(data);
};

export const trackPageView = (baseOption: PageViewBaseOption) => {
  sendPageViewForWave2({
    ...baseOption
  });
};

/**
 * Track an event such as clicking event on GoogleAnalytics
 * via sending data to dataLayer of window object.
 *
 * @param data - Analytics data
 * @param clearEcommerce - Optional boolean to clear previous ecommerce object before pushing event (default: false)
 */
export const sendTrackEvent = (
  data: GATrackEventType | GAGlobalOptionType | GAStylingType | GAFilterType,
  clearEcommerce = false
): void => {
  if (!isClient) {
    return;
  }
  if (!window.dataLayer) {
    return;
  }

  const eventData = {
    ...data,
    event: data.event || "trackEvent"
  };

  if (
    window.UQCustomerAppAndroid &&
    window.UQCustomerAppAndroid.receiveAnalyticsData
  ) {
    // Android App
    window.UQCustomerAppAndroid.receiveAnalyticsData(JSON.stringify(eventData));
  } else if (
    window.webkit &&
    window.webkit.messageHandlers.receiveAnalyticsData &&
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage
  ) {
    // iOS App
    window.webkit.messageHandlers.receiveAnalyticsData.postMessage(eventData);
  }
  // Web Browser and Native App
  if (clearEcommerce) {
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  }
  window.dataLayer.push(data);
};

type GAItemEventType = {
  brand: BRAND_LIST;
  country: COUNTRY;
  productData: ProductGAInfoType;
  styling_id?: string;
};

type GAStylingEventType = {
  styling_id: string;
  style_user_id: string;
};

type GAUserEventType = {
  style_user_id: string;
  style_user_index: number;
};

type GAFilterEventType = {
  filter_method?: string;
  result_count: number;
  sort_type: string;
  tag_list_1: string;
  tag_list_2: string;
};

type GAItemListEventType = {
  brand: BRAND_LIST;
  country: COUNTRY;
  currency: string;
  productList: ProductGAInfoType[];
  styling_id: string;
};

// ADD_TO_WISHLIST
export const trackAddToWishListEvent = (data: GAItemEventType) =>
  sendTrackEvent(
    {
      event: ADD_TO_WISHLIST,
      styling_id: data.styling_id,
      ecommerce: {
        currency: data.productData?.currency || "",
        value: data.productData?.displayPrice ?? 0,
        items: [
          {
            ...getGAPropsForAddWishlist(
              data.brand,
              data.productData,
              data.productData?.index ?? 0
            )
          }
        ]
      }
    },
    true
  );

// REMOVE_FROM_WISHLIST
export const trackRemoveFromWishListEvent = (data: GAItemEventType) =>
  sendTrackEvent(
    {
      event: REMOVE_FROM_WISHLIST,
      styling_id: data.styling_id,
      ecommerce: {
        currency: data.productData?.currency || "",
        value: data.productData?.displayPrice ?? 0,
        custom_item_id: data.productData?.itemId || "",
        custom_item_product_id: data.productData?.productId || "",
        custom_item_l1_id: "",
        items: [
          {
            ...getGAPropsForRemoveWishlist(
              data.brand,
              data.productData,
              data.productData?.index ?? 0
            )
          }
        ]
      }
    },
    true
  );

export const getLoginState = (): LoginStateType => {
  if (!isClient) {
    return LoginStateType.NotLogin;
  }
  if (getIsLoggedIn()) {
    return LoginStateType.EC;
  }
  if (getIsECGuestLoggedIn()) {
    return LoginStateType.Guest;
  }
  return LoginStateType.NotLogin;
};

// SELECT_ITEM
export const trackSelectItemEvent = (data: GAItemEventType) =>
  sendTrackEvent(
    {
      event: SELECT_ITEM,
      styling_id: data.styling_id,
      ecommerce: {
        items: [
          {
            ...getBaseGAItemProps({
              brand: data.brand,
              product: data.productData,
              index: data.productData?.index ?? 0
            })
          }
        ]
      }
    },
    true
  );

// SELECT_STYLING
export const trackSelectStylingEvent = (data: GAStylingEventType) =>
  sendTrackEvent({
    event: SELECT_STYLING,
    styling_id: data.styling_id,
    style_user_id: data.style_user_id
    // result_count: data.result_count,
    // sort_type: data.sort_type || NONE,
    // tag_list_1: data.tag_list_1 || NONE,
    // tag_list_2: data.tag_list_2 || NONE
  });

// ADD_TO_STYLING_WISHLIST
export const trackAddToStylingWishlistEvent = (data: GAStylingEventType) =>
  sendTrackEvent({
    event: ADD_TO_STYLING_WISHLIST,
    styling_id: data.styling_id,
    style_user_id: data.style_user_id
  });

// REMOVE_FROM_STYLING_WISHLIST
export const trackRemoveFromStylingWishlistEvent = (data: GAStylingEventType) =>
  sendTrackEvent({
    event: REMOVE_FROM_STYLING_WISHLIST,
    styling_id: data.styling_id,
    style_user_id: data.style_user_id
  });

// VIEW_STYLING
export const trackViewStylingEvent = (data: GAStylingEventType) =>
  sendTrackEvent({
    event: VIEW_STYLING,
    styling_id: data.styling_id,
    style_user_id: data.style_user_id
  });

// CLICK_RECOMMENDED_USER
export const trackClickRecommendUserEvent = (data: GAUserEventType) =>
  sendTrackEvent({
    event: CLICK_RECOMMENDED_USER,
    style_user_id: data.style_user_id,
    style_user_index: data.style_user_index
  });

// APPLY_FILTER
export const trackApplyFilterEvent = (data: GAFilterEventType) =>
  sendTrackEvent({
    event: APPLY_FILTER,
    filter_method: data.filter_method,
    result_count: data.result_count,
    sort_type: data.sort_type || NONE,
    tag_list_1: data.tag_list_1 || NONE,
    tag_list_2: data.tag_list_2 || NONE
  });
export const trackFilterApplyEvent = (
  filterStr: string,
  sortType: string,
  resultCount: number,
  method: "manual" | "tag_filter_group"
): void => {
  const [tagList1, tagList2] = splitTagList(filterStr);

  trackApplyFilterEvent({
    filter_method: method,
    result_count: resultCount,
    sort_type: sortType,
    tag_list_1: tagList1,
    tag_list_2: tagList2
  });
};

// VIEW_ITEM_LIST
export const trackViewListItems = (data: GAItemListEventType) => {
  sendTrackEvent(
    {
      event: VIEW_ITEM_LIST,
      styling_id: data.styling_id,
      ecommerce: {
        currency: data.currency || "",
        items: data.productList.map((product, index) => {
          const {
            item_variant,
            item_color_code,
            item_size,
            prc_grp_seq_num,
            mk_product_id,
            ...baseProps
          } = getBaseGAItemProps({
            brand: data.brand,
            product: product,
            index
          });
          return {
            ...baseProps
          };
        })
      }
    },
    true
  );
};
